/**
 * Organizer-path rules for the PRE-APP surfaces (the quiz funnels, the generator handoff), where
 * there is no place to show the in-app question or the switch prompt — so nothing here may ever
 * end in an error the visitor can see.
 *
 * Deliberately dependency-free (no services, no constants): it is imported by the funnels and by
 * the unit tests, and anything heavier drags the whole component graph into node.
 */

export type OrganizerPathValue = 'one_off' | 'community';

/**
 * Which path a quiz signup asks for: THE AUDIENCE ANSWER, whatever the format. A visitor who said
 * "one-off" is on the one-off side for a tournament, an Americano AND a league — a league there is
 * one event like any other, free to set up, with the Event Pass (sized by its field) asked for at
 * the first score. (It used to be "the format wins": a league forced regular events, because a
 * one-off club could not create one. It can now, so the answer stands.)
 *
 * With no audience answer at all the explicit value is `community`, which is what the column
 * default always meant; sent explicitly so the club counts as having answered and is never
 * stopped mid-funnel. `eventKind` is kept in the signature so the call sites stay the one place
 * a future per-format rule would go.
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function quizOrganizerPath(_eventKind: string | undefined | null, audience: string | undefined | null): OrganizerPathValue {
    return audience === 'one_off' ? 'one_off' : 'community';
}

const PASS_GATE_CODES = ['event_pass_required', 'event_pass_topup_required'];

/**
 * Is this the one-off path's pass gate (go-live or top-up)? True for a rejected error object, a
 * resolved `{error, code}` body, or an Error that had the body copied onto it. A pre-app surface
 * has no offer modal, so it must never show this as an error — it hands over to the event page,
 * where the offer opens (see queuePostCreateOffer).
 */
export const isPassGate = (x: unknown): boolean =>
    !!x && typeof x === 'object' && PASS_GATE_CODES.includes((x as {code?: string}).code ?? '');

/** sessionStorage key the event page's OneOffPassBanner reads to open the offer once. */
export const POST_CREATE_OFFER_KEY = (uid: string) => `oneoff-post-create:${uid}`;
/** Fired by the quiz welcome tour when it closes, so the offer never opens underneath it. */
export const QUIZ_TOUR_CLOSED_EVENT = 'sp:quiz-tour-closed';
export const QUIZ_TOUR_SEEN_KEY = 'sp_quiz_tour_seen';

/**
 * Ask the event page to open the pass offer once when the organizer lands on it. Harmless when
 * nothing is owed: the banner opens the offer only if the server's own resolver says this event
 * needs one, so a small event, a community club or a legacy club sees nothing.
 * `source: 'quiz'` has the landing recorded as a `post_create` impression (the social formats'
 * create response records theirs server-side). A gate payload, when there is one, rides along as
 * the fallback offer.
 */
export function queuePostCreateOffer(uid: string, gatePayload?: unknown): void {
    try {
        sessionStorage.setItem(POST_CREATE_OFFER_KEY(uid), JSON.stringify({...(isPassGate(gatePayload) ? gatePayload as object : {}), source: 'quiz'}));
    } catch { /* storage off — the banner on the event page still shows the offer */ }
}

const PATH_CODES = ['community_path_required', 'organizer_path_required'];

type PathPayload = {code?: string; club_uid?: string | null};

/**
 * Safety net for a create call made from a pre-app surface. If the server answers with a path
 * gate — `community_path_required` (a one-off club creating a league) or `organizer_path_required`
 * (a club that never answered) — settle the path and retry the create ONCE, silently. The visitor
 * must never see either message during signup; a second refusal is thrown like any other error.
 *
 * Handles both API shapes: a rejected error object and a resolved `{error, code}` body.
 * `setPath` is injected so this stays free of the services module (and testable).
 */
export async function withPathSafetyNet<T>(
    create: () => Promise<T>,
    setPath: (clubUid: string | undefined, path: OrganizerPathValue) => Promise<unknown>,
    opts: {fallbackPath: OrganizerPathValue; clubUid?: string},
): Promise<T> {
    const isPathGate = (x: unknown): x is PathPayload => !!x && typeof x === 'object' && PATH_CODES.includes((x as PathPayload).code ?? '');
    const settle = (payload: PathPayload) => setPath(
        payload.club_uid || opts.clubUid,
        // A league needs regular events, whatever was answered; an unanswered club gets the
        // funnel's own answer.
        payload.code === 'community_path_required' ? 'community' : opts.fallbackPath,
    );

    let result: T;
    try {
        result = await create();
    } catch (err) {
        if (!isPathGate(err)) throw err;
        await settle(err);

        return create();
    }

    if (isPathGate(result)) {
        await settle(result);

        return create();
    }

    return result;
}
